import React, { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { TYPE_ORDER, ENTITY_TYPES, resolveType, allTypes } from "@/lib/entities";
import { Search, Edit3, CheckCircle2, Sparkles, Plus, GripVertical, Skull, Laugh, MessageSquareQuote, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function CodexGrid({ entities, onEdit, onToggleIncluded, onToggleLoadout, onToggleKia, onAdd, onInsert, onSuggestVoices, suggestingVoices = false, customTypes = [], compact = false }) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const types = useMemo(() => allTypes(customTypes), [customTypes]);

  const counts = useMemo(() => {
    const c = { all: entities.length };
    types.forEach((t) => (c[t.key] = 0));
    entities.forEach((e) => { c[e.type] = (c[e.type] || 0) + 1; });
    return c;
  }, [entities, types]);

  const filtered = useMemo(() => {
    return entities.filter((e) => {
      if (filter !== "all" && e.type !== filter) return false;
      if (query) {
        const q = query.toLowerCase();
        return e.name.toLowerCase().includes(q) ||
          (e.description || "").toLowerCase().includes(q) ||
          (e.detail || "").toLowerCase().includes(q);
      }
      return true;
    });
  }, [entities, filter, query]);

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="p-3 space-y-3 border-b border-border">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search the codex…"
            className="pl-9 h-9" data-testid="codex-search-input" />
        </div>
        {onSuggestVoices && (
          <Button size="sm" variant="outline" className="w-full gap-1.5 h-8 text-xs" onClick={onSuggestVoices} disabled={suggestingVoices}
            data-testid="codex-suggest-voices" title="Reads the book so far and fills in 'How they speak' for main characters that don't have one yet (2 credits)">
            {suggestingVoices ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquareQuote className="w-3.5 h-3.5" />}
            {suggestingVoices ? "Listening to the characters…" : "Suggest character voices (2 credits)"}
          </Button>
        )}
        <div className="flex flex-wrap gap-1.5">
          <FilterChip active={filter === "all"} onClick={() => setFilter("all")} label="All" count={counts.all} testid="codex-filter-all" />
          {types.map((t) => (
            <FilterChip key={t.key} active={filter === t.key} onClick={() => setFilter(t.key)}
              label={t.plural} count={counts[t.key] || 0} testid={`codex-filter-${t.key}`} dotColor={t.color} />
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3">
        {filtered.length === 0 ? (
          <div className="text-center py-14 px-4">
            <Sparkles className="w-9 h-9 text-muted-foreground/50 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground mb-4">
              {entities.length === 0 ? "No vital details yet. Run an AI scan while writing, or add one manually." : "No details match your search."}
            </p>
            {onAdd && (
              <Button size="sm" variant="outline" onClick={onAdd} data-testid="codex-empty-add" className="gap-1.5">
                <Plus className="w-4 h-4" /> Add detail
              </Button>
            )}
          </div>
        ) : (
          <div className={compact ? "space-y-2.5" : "grid sm:grid-cols-2 xl:grid-cols-3 gap-4"}>
            {filtered.map((e) => (
              <EntityCard key={e.id} entity={e} onEdit={onEdit} onToggleIncluded={onToggleIncluded} onToggleLoadout={onToggleLoadout} onToggleKia={onToggleKia} onInsert={onInsert} customTypes={customTypes} compact={compact} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, label, count, testid, dotColor }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors border
        ${active ? "bg-primary text-primary-foreground border-primary" : "bg-secondary/50 border-border hover:bg-secondary text-foreground"}`}>
      {dotColor && <span className="w-2 h-2 rounded-full" style={{ background: dotColor }} />}
      {label} <span className="opacity-60">{count}</span>
    </button>
  );
}

function EntityCard({ entity, onEdit, onToggleIncluded, onToggleLoadout, onToggleKia, onInsert, customTypes, compact }) {
  const meta = resolveType(entity.type, customTypes);
  const Icon = meta.icon;
  const kia = !!entity.kia;
  const loadoutOn = entity.loadout_owner === true
    ? true
    : entity.loadout_owner === false
      ? false
      : (meta.isOwner || !!entity.is_source);
  return (
    <div data-testid={`codex-card-${entity.id}`}
      className={`group rounded-xl border bg-card p-3.5 transition-all hover:border-primary/50 hover:shadow-sm relative
        ${kia ? "opacity-70 border-border" : entity.included ? "border-primary/40" : "border-border"}`}>
      <div className={`flex items-start gap-3 ${kia ? "grayscale" : ""}`}>
        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
          style={{ backgroundColor: meta.color + "26", color: meta.color }}>
          <Icon className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h4 className="font-display font-bold text-lg leading-tight truncate">{entity.name}</h4>
            {entity.included && !kia && <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <Badge variant="secondary" className="text-[10px] uppercase tracking-wider">{meta.label}</Badge>
            {entity.auto_detected && (
              <Badge data-testid={`auto-badge-${entity.id}`}
                className="text-[10px] uppercase tracking-wider bg-primary/10 text-primary border border-primary/30 gap-1">
                <Sparkles className="w-3 h-3" /> Auto
              </Badge>
            )}
            {kia && (
              <Badge data-testid={`kia-badge-${entity.id}`}
                className="text-[10px] uppercase tracking-wider bg-destructive/15 text-destructive border border-destructive/30 gap-1">
                <Skull className="w-3 h-3" /> KIA
              </Badge>
            )}
            {entity.comic && !kia && (
              <Badge data-testid={`comic-badge-${entity.id}`}
                className="text-[10px] uppercase tracking-wider bg-fuchsia-500/15 text-fuchsia-400 border border-fuchsia-500/30 gap-1">
                <Laugh className="w-3 h-3" /> Wit
              </Badge>
            )}
          </div>
        </div>
        {onToggleKia && (
          <button onClick={() => onToggleKia(entity, !kia)} data-testid={`kia-toggle-${entity.id}`}
            title={kia ? "Marked KIA — click to bring back to active" : "Mark as KIA (frozen, excluded from Gear Sync)"}
            className={`transition-opacity shrink-0 ${kia ? "text-destructive opacity-100" : "opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"}`}>
            <Skull className="w-4 h-4" />
          </button>
        )}
        <button onClick={() => onEdit(entity)} data-testid={`update-entity-button-${entity.id}`}
          className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-primary shrink-0">
          <Edit3 className="w-4 h-4" />
        </button>
      </div>
      {entity.description && <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{entity.description}</p>}
      {entity.detail && (
        <div className="mt-2 inline-block font-mono-code text-xs px-2 py-1 rounded bg-secondary text-foreground">
          {entity.detail}
        </div>
      )}
      {onInsert && (
        <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-0.5">Drag to insert:</span>
          <InsertChip value={entity.name} onInsert={onInsert} testid={`insert-name-${entity.id}`} />
          {entity.nickname && <InsertChip value={entity.nickname} onInsert={onInsert} testid={`insert-nickname-${entity.id}`} />}
          {entity.detail && <InsertChip value={entity.detail} onInsert={onInsert} mono testid={`insert-detail-${entity.id}`} />}
          {(entity.contents || []).length > 0 && (
            <InsertChip
              value={(entity.contents || []).map((c) => (c.qty > 1 ? `${c.qty} ${c.name}` : c.name)).join(", ")}
              label={`📦 Contents list (${entity.contents.length})`}
              onInsert={onInsert} testid={`insert-contents-${entity.id}`} />
          )}
        </div>
      )}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-border gap-2">
        <span className="text-xs text-muted-foreground shrink-0">{entity.mentions || 1}× tracked</span>
        <div className="flex items-center gap-3">
          {onToggleLoadout && (
            <label className="flex items-center gap-1.5 cursor-pointer" title="Show this entry as an owner in the Loadouts view">
              <span className="text-xs text-muted-foreground">Loadout</span>
              <Switch checked={loadoutOn} onCheckedChange={(v) => onToggleLoadout(entity, v)}
                data-testid={`loadout-owner-toggle-${entity.id}`} />
            </label>
          )}
          <label className="flex items-center gap-1.5 cursor-pointer">
            <span className="text-xs text-muted-foreground">Included</span>
            <Switch checked={!!entity.included} onCheckedChange={() => onToggleIncluded(entity)}
              data-testid={`mark-included-toggle-${entity.id}`} />
          </label>
        </div>
      </div>
    </div>
  );
}


function InsertChip({ value, onInsert, mono, testid, label }) {
  return (
    <span
      draggable
      data-testid={testid}
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", value);
        e.dataTransfer.effectAllowed = "copy";
      }}
      onClick={() => onInsert(value)}
      title="Drag into the manuscript, or click to insert at the cursor"
      className={`inline-flex items-center gap-1 max-w-[13rem] truncate rounded-md border border-border bg-background px-1.5 py-0.5 text-xs cursor-grab active:cursor-grabbing hover:border-primary/60 hover:text-primary transition-colors ${mono ? "font-mono-code" : ""}`}
    >
      <GripVertical className="w-3 h-3 opacity-50 shrink-0" />
      <span className="truncate">{label || value}</span>
    </span>
  );
}
